import { useCallback, useMemo, useState } from 'react';

import { PostFilter } from '../../../../entities/post/models';
import { showDrillDownContextMenu } from '../../conversation/overlay/show-drill-down-context-menu';
import { useFilteredPostsNavigation } from '../../filtered-posts/hook/use-filtered-posts-navigation';
import { useFilteredProfilesNavigation } from '../../filtered-profiles/hook/use-filtered-profiles-navigation';

import { METRIC, type ThemeStatisticData } from '@/entities/insight';
import { useThemes } from '../../shared/hooks/use-themes';
import { usePublicFilter } from '../../shared/public-filter/use-public-filter';

interface UseUnifiedThemeTableProps {
  queryId: string;
  startDate: string;
  endDate: string;
}

export function useUnifiedThemeTable({ queryId, startDate, endDate }: UseUnifiedThemeTableProps) {
  const navigateToFilteredPosts = useFilteredPostsNavigation();
  const navigateToFilteredProfiles = useFilteredProfilesNavigation();
  const { publicFilter } = usePublicFilter(queryId);

  const [metric, setMetric] = useState<METRIC>(METRIC.MENTION);

  // Fetch top 5 themes data (no sentiment filter for overview)
  const { data: themes, isLoading } = useThemes({
    queryId,
    startDate,
    endDate,
    view_by: PostFilter.Theme,
    limit: 5,
    publicFilter,
  });

  // Use themes directly since we're already limiting to 5 from API
  const topThemes = useMemo(() => {
    if (!themes) return [];
    return themes as ThemeStatisticData[];
  }, [themes]);

  const handleRowClick = useCallback(
    async (theme: ThemeStatisticData, event: React.MouseEvent) => {
      const action = await showDrillDownContextMenu({
        position: { x: event.clientX, y: event.clientY },
      });

      const filter = {
        [PostFilter.Theme]: theme.name,
        [PostFilter.Sentiment]: theme.sentiment,
      };

      switch (action) {
        case 'view-posts':
          await navigateToFilteredPosts({
            dimension: PostFilter.Theme,
            filter,
          });
          break;
        case 'view-profiles':
          await navigateToFilteredProfiles({
            dimension: PostFilter.Theme,
            filter,
          });
          break;
        case 'close':
          break;
      }
    },
    [navigateToFilteredPosts, navigateToFilteredProfiles],
  );

  return {
    topThemes,
    metric,
    setMetric,
    handleRowClick,
    isLoading,
  };
}
